import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Button, Drawer, Empty, Input, Select, Space, Table, Tag, Timeline, Tooltip } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import {
  InfoCircleOutlined,
  LeftOutlined,
  MessageOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { useAuthSession } from '@/lib/session'
import { useToast } from '@/contexts/ToastContext'
import {
  isTicketReference,
  replyErrorMessage,
  supportErrorMessage,
  TICKET_STATUSES,
} from '@/api/advisorySupport'
import {
  useAllSupportTickets,
  useChangeTicketStatusMutation,
  useFetchLatestTicketStatus,
  useRefreshSupportConsole,
  useSendTicketReplyMutation,
  useTicketHistory,
} from '@/features/advisory-ai/hooks'
import {
  SUPPORT_AREAS,
  TICKET_STATUS_ADMIN_LABEL,
} from '@/features/advisory-ai/assistant/mockData'
import type {
  AdminSupportTicket,
  TicketStatus,
  TicketStatusValue,
} from '@/features/advisory-ai/assistant/types'
import { TicketReplies } from './components/TicketReplies'
import { TicketReplyComposer } from './components/TicketReplyComposer'
import { exactTime, when } from './utils'

/**
 * The support console — every ticket, across every client.
 *
 * Admin-only, gated by the route descriptor's `visibility.when` rather than a
 * check here, so the hidden nav entry and the redirect cannot disagree. The
 * server refuses non-admins independently.
 *
 * `clientId` is the admin's session context, not a filter — the console spans
 * clients by design. Narrowing to one is `clientFilter` (spec §3.3).
 *
 * **No column sorting**: the list endpoint states no order, and a per-page
 * client sort would reorder only the rows already fetched.
 */

const STATUS_COLOUR: Record<TicketStatusValue, string> = {
  open: 'orange',
  reviewing: 'purple',
  awaiting: 'blue',
  closed: 'default',
  unknown: 'default',
}

const STATUS_OPTIONS = TICKET_STATUSES.map((value) => ({
  value,
  label: TICKET_STATUS_ADMIN_LABEL[value],
}))

/** A status the UI cannot set shows as a placeholder, not as a raw value the Select has no option for. */
const selectableStatus = (status: TicketStatusValue): TicketStatus | undefined =>
  status === 'unknown' ? undefined : status

const SEARCH_DEBOUNCE_MS = 350

function SectionLabel({ children }: { children: string }) {
  return (
    <div className="mb-1 text-[9.5px] font-extrabold uppercase tracking-[0.12em] text-gray-400">
      {children}
    </div>
  )
}

/** The status log for one ticket, oldest first. */
function TicketHistory({ ticketId, clientId }: { ticketId: string; clientId: string }) {
  const query = useTicketHistory(ticketId, clientId)

  if (query.isLoading) return <div className="text-[12px] text-gray-400">Loading history…</div>
  if (query.isError) return <div className="text-[12px] text-gray-400">Could not load the history.</div>
  if (!query.data?.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No changes recorded" />

  return (
    <Timeline
      items={query.data.map((entry) => ({
        children: (
          <div>
            <div className="text-[12.5px] text-gray-800">
              {entry.fromStatus
                ? `${TICKET_STATUS_ADMIN_LABEL[entry.fromStatus]} → ${TICKET_STATUS_ADMIN_LABEL[entry.toStatus]}`
                : 'Raised'}
              {entry.changedByName && (
                <span className="text-gray-500"> by {entry.changedByName}</span>
              )}
            </div>
            {/* Exact time on hover: relative is right for scanning, wrong for
                "did support reply before or after the user did?". */}
            <Tooltip title={exactTime(entry.changedAt)}>
              <span className="text-[11px] text-gray-400">{when(entry.changedAt)}</span>
            </Tooltip>
          </div>
        ),
      }))}
    />
  )
}

export default function AdvicePrepSupportConsole() {
  const navigate = useNavigate()
  const session = useAuthSession()
  const { addToast } = useToast()
  const clientId = session?.currentClientId || ''
  const defaultPageSize = session?.defaultPageSize && session.defaultPageSize > 0 ? session.defaultPageSize : 20

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<TicketStatus | ''>('')
  const [area, setArea] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  // Only the row as it was when clicked. What the drawer shows comes from the API — see `openTicket`.
  const [openedRow, setOpenedRow] = useState<AdminSupportTicket | null>(null)
  // By ticket, so closing the drawer or opening another one does not throw away a half-written reply.
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({})
  const [replyErrors, setReplyErrors] = useState<Record<string, string>>({})
  // "I already replied by email", per ticket and cleared once the reply has gone.
  const [replyByEmail, setReplyByEmail] = useState<Record<string, boolean>>({})
  /**
   * Which tickets have a reply in flight.
   *
   * Not the mutation's own `isPending`/`variables`: one mutation instance serves
   * the whole console, and those track only the most recent dispatch. Send on A,
   * send on B while A is still going, and A's composer reads as idle — with its
   * draft and its email tick intact, so the reply can go out twice.
   */
  const [replyInFlight, setReplyInFlight] = useState<Record<string, boolean>>({})

  // Here, not in the composer: the drawer can close while a reply is in flight,
  // and the draft must still be cleared once it has gone.
  const { mutate: sendReply } = useSendTicketReplyMutation(clientId, {
    onSent: (ticketId, email) => {
      setReplyInFlight((current) => ({ ...current, [ticketId]: false }))
      setReplyDrafts((current) => ({ ...current, [ticketId]: '' }))
      setReplyErrors((current) => ({ ...current, [ticketId]: '' }))
      // Cleared even on a mismatch: the reply did save, so re-sending would post
      // it twice. The warning is the remedy, not another attempt.
      setReplyByEmail((current) => ({ ...current, [ticketId]: false }))
      // Both directions warn. Asked for and not saved means a notice went out
      // that shouldn't have; saved without being asked for is worse — the
      // customer was never told there is a reply waiting for them.
      if (email.requested !== email.saved) {
        addToast(
          email.requested
            ? 'Reply posted, but it was not marked as sent by email — the customer may have been emailed a notice.'
            : 'Reply posted and marked as sent by email, which was not asked for — the customer was not emailed a notice.',
          'warning',
          8000
        )
        return
      }
      addToast(email.saved ? 'Reply recorded — no notice emailed' : 'Reply posted to the thread', 'success')
    },
    onFailed: (ticketId, error) => {
      setReplyInFlight((current) => ({ ...current, [ticketId]: false }))
      setReplyErrors((current) => ({ ...current, [ticketId]: replyErrorMessage(error) }))
    },
  })

  // Debounced: the endpoint searches subject, body and reference.
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  // Back to page 1 on any filter change, or page 4 of a one-page result reads
  // as "no tickets match".
  useEffect(() => {
    setPage(1)
  }, [search, status, area, pageSize])

  const query = useAllSupportTickets({
    clientId,
    status,
    area,
    search,
    skip: (page - 1) * pageSize,
    take: pageSize,
  })

  const refreshConsole = useRefreshSupportConsole()
  const [refreshing, setRefreshing] = useState(false)
  const refresh = () => {
    setRefreshing(true)
    refreshConsole().finally(() => setRefreshing(false))
  }

  // The status log is written by every change — the console's, a reply's, and
  // the customer's answer in Preppy — and does not depend on the list's filters
  // or page, so its latest entry is the ticket's status on the server.
  const openHistory = useTicketHistory(openedRow?.id ?? '', clientId, Boolean(openedRow))
  const listRow = openedRow ? query.data?.tickets.find((t) => t.id === openedRow.id) : undefined
  const latestHistory = openHistory.data?.[openHistory.data.length - 1]
  const openTicket: AdminSupportTicket | null = openedRow
    ? {
        ...openedRow,
        ...listRow,
        status: latestHistory?.toStatus ?? listRow?.status ?? openedRow.status,
      }
    : null

  const { mutate: changeStatus, isPending: statusChanging, variables: changing } =
    useChangeTicketStatusMutation(clientId)

  // Not optimistic: the new status shows once the refetch the mutation triggers brings it back.
  const changeTicketStatus = useCallback(
    (ticketId: string, next: TicketStatus) =>
      changeStatus(
        { ticketId, status: next },
        { onError: (error) => addToast(supportErrorMessage(error), 'error') }
      ),
    [addToast, changeStatus]
  )

  const fetchLatestStatus = useFetchLatestTicketStatus(clientId)

  // Only from `open`: an awaiting or closed ticket opened just to read it would
  // otherwise tell the customer somebody is looking at it again. Decided on a
  // fresh read, not the row — the list can be minutes old, and the status
  // endpoint would happily undo another admin's reply or close.
  const openTicketRow = useCallback(
    (row: AdminSupportTicket) => {
      setOpenedRow(row)
      if (row.status !== 'open') return
      fetchLatestStatus(row.id)
        .then((latest) => {
          if (latest === 'open') changeTicketStatus(row.id, 'reviewing')
        })
        // No fresh status, no change: acting on the stale row is the thing this avoids.
        .catch(() => undefined)
    },
    [changeTicketStatus, fetchLatestStatus]
  )

  // Emails link here as `?ticket=DI-4821`. Found through the list's own search,
  // opened as a click would open it, then consumed so a refresh does not reopen it.
  const [searchParams, setSearchParams] = useSearchParams()
  const linkedReference = searchParams.get('ticket')?.trim() ?? ''
  const linkIsValid = isTicketReference(linkedReference)
  const [linkProblem, setLinkProblem] = useState('')
  const linkedQuery = useAllSupportTickets(
    { clientId, search: linkIsValid ? linkedReference : '', skip: 0, take: 10 },
    linkIsValid
  )
  const handledLink = useRef('')
  useEffect(() => {
    if (!linkedReference || handledLink.current === linkedReference) return
    // Placeholder data is the previous search's rows, not this one's.
    if (linkIsValid && ((!linkedQuery.isSuccess && !linkedQuery.isError) || linkedQuery.isPlaceholderData)) return
    handledLink.current = linkedReference

    const row = linkedQuery.data?.tickets.find(
      (t) => t.reference.toLowerCase() === linkedReference.toLowerCase()
    )
    if (!linkIsValid) {
      // Not echoed: the text is whatever the link says.
      setLinkProblem("That ticket link isn't valid.")
    } else if (row) {
      setLinkProblem('')
      openTicketRow(row)
    } else if (linkedQuery.isError) {
      setLinkProblem(`Couldn't open ticket ${linkedReference} just now. Search for it above, or open the link again.`)
    } else {
      setLinkProblem(`Ticket ${linkedReference} wasn't found`)
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('ticket')
        return next
      },
      { replace: true }
    )
  }, [
    linkIsValid,
    linkedQuery.data,
    linkedQuery.isError,
    linkedQuery.isPlaceholderData,
    linkedQuery.isSuccess,
    linkedReference,
    openTicketRow,
    setSearchParams,
  ])

  const columns: ColumnsType<AdminSupportTicket> = useMemo(
    () => [
      {
        title: 'Reference',
        dataIndex: 'reference',
        key: 'reference',
        width: 110,
        render: (value: string, row) => (
          <a className="feature-link font-medium" onClick={() => openTicketRow(row)}>
            {value}
          </a>
        ),
      },
      {
        title: 'Subject',
        dataIndex: 'subject',
        key: 'subject',
        render: (value: string, row) => (
          <div className="min-w-0">
            <div className="truncate font-medium text-gray-900">{value}</div>
            <div className="truncate text-[11px] text-gray-400">{row.area}</div>
          </div>
        ),
      },
      {
        title: 'Raised by',
        dataIndex: 'raisedByName',
        key: 'raisedByName',
        width: 200,
        render: (value: string, row) => (
          <div className="min-w-0">
            <div className="truncate text-gray-800">{value || '—'}</div>
            <div className="truncate text-[11px] text-gray-400">{row.raisedByEmail}</div>
          </div>
        ),
      },
      {
        title: 'Client',
        dataIndex: 'clientName',
        key: 'clientName',
        width: 170,
        render: (value: string, row) => (
          <div className="min-w-0">
            <div className="truncate text-gray-800">{value || '—'}</div>
            {row.platformName && <div className="truncate text-[11px] text-gray-400">{row.platformName}</div>}
          </div>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 160,
        render: (value: TicketStatusValue, row) => (
          <Select<TicketStatus>
            size="small"
            value={selectableStatus(value)}
            placeholder={TICKET_STATUS_ADMIN_LABEL.unknown}
            options={STATUS_OPTIONS}
            className="w-full"
            loading={statusChanging && changing?.ticketId === row.id}
            onChange={(next) => changeTicketStatus(row.id, next)}
          />
        ),
      },
      {
        title: 'Updated',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 150,
        render: (value: string) => <span className="text-[12px] text-gray-500">{when(value)}</span>,
      },
    ],
    [changeTicketStatus, changing?.ticketId, openTicketRow, statusChanging]
  )

  const replyText = openTicket ? replyDrafts[openTicket.id] ?? '' : ''
  const setReplyText = (ticketId: string, text: string) =>
    setReplyDrafts((current) => ({ ...current, [ticketId]: text }))

  if (!clientId) {
    return (
      <Alert
        type="warning"
        showIcon
        message="No active client selected"
        description="Select a client to open the AdvicePrep support console."
      />
    )
  }

  return (
    <FeatureThemeProvider featureKey="advisoryIntel" withAntd className="w-full">
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4">
        <Space size={8}>
          {/* Not in the sidebar, so it carries its own way back. */}
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={() => navigate('/adviceprep')}
            aria-label="Back to AdvicePrep"
          />
          <h1 className="font-display text-2xl font-bold text-gray-900">AdvicePrep Support</h1>
          <Tooltip title="Every AdvicePrep ticket, across every client. Tickets raised on the product-wide support form are emailed and are not listed here.">
            <InfoCircleOutlined className="text-gray-400" />
          </Tooltip>
        </Space>

        <Space size={8} wrap>
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Subject, body or reference"
            suffix={<SearchOutlined />}
            allowClear
            className="w-64"
          />
          <Select<TicketStatus | ''>
            value={status}
            onChange={setStatus}
            className="w-40"
            options={[{ value: '', label: 'All statuses' }, ...STATUS_OPTIONS]}
          />
          <Select<string>
            value={area}
            onChange={setArea}
            className="w-56"
            options={[
              { value: '', label: 'All areas' },
              ...SUPPORT_AREAS.map((value) => ({ value, label: value })),
            ]}
          />
          <Button icon={<ReloadOutlined />} loading={refreshing} onClick={refresh}>
            Refresh
          </Button>
        </Space>
      </div>

      {linkProblem ? (
        <div className="pb-4">
          <Alert type="warning" showIcon closable onClose={() => setLinkProblem('')} message={linkProblem} />
        </div>
      ) : null}

      {query.error ? (
        <div className="pb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load tickets"
            description={query.error instanceof Error ? query.error.message : 'Unknown error'}
          />
        </div>
      ) : null}

      <Table<AdminSupportTicket>
        rowKey="id"
        columns={columns}
        dataSource={query.data?.tickets ?? []}
        loading={query.isLoading || query.isFetching}
        scroll={{ x: 1000 }}
        pagination={
          {
            current: page,
            pageSize,
            total: query.data?.totalCount ?? 0,
            showSizeChanger: true,
            // 100 is the server's cap on `take`.
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `${total} ticket${total === 1 ? '' : 's'}`,
            onChange: (nextPage, nextSize) => {
              setPage(nextPage)
              if (nextSize) setPageSize(nextSize)
            },
          } satisfies TablePaginationConfig
        }
        locale={{
          emptyText: query.isLoading ? 'Loading...' : <Empty description="No tickets match those filters" />,
        }}
      />

      <Drawer
        open={Boolean(openTicket)}
        onClose={() => setOpenedRow(null)}
        width={640}
        title={openTicket ? `${openTicket.reference} · ${openTicket.area}` : ''}
        // The drawer's mask covers the page's own Refresh.
        extra={
          <Button size="small" icon={<ReloadOutlined />} loading={refreshing} onClick={refresh}>
            Refresh
          </Button>
        }
      >
        {/* The drawer is portalled out of the page's theme wrapper, so it needs its own for the --feature-* vars. */}
        {openTicket && (
          <FeatureThemeProvider featureKey="advisoryIntel" withAntd className="space-y-5">
            <div>
              <div className="text-[15px] font-bold leading-snug text-gray-900">{openTicket.subject}</div>
              <div className="mt-1 text-[12px] text-gray-400">
                {openTicket.raisedByName} · {openTicket.raisedByEmail}
              </div>
              <div className="text-[12px] text-gray-400">
                {openTicket.clientName}
                {openTicket.platformName ? ` · ${openTicket.platformName}` : ''}
                {openTicket.raisedFrom ? ` · from ${openTicket.raisedFrom}` : ''}
                {when(openTicket.raisedAt) ? ` · raised ${when(openTicket.raisedAt)}` : ''}
              </div>
            </div>

            <Space size={8}>
              <Tag color={STATUS_COLOUR[openTicket.status]} className="m-0">
                {TICKET_STATUS_ADMIN_LABEL[openTicket.status]}
              </Tag>
              <Select<TicketStatus>
                size="small"
                value={selectableStatus(openTicket.status)}
                placeholder={TICKET_STATUS_ADMIN_LABEL.unknown}
                options={STATUS_OPTIONS}
                className="w-40"
                loading={statusChanging && changing?.ticketId === openTicket.id}
                onChange={(next) => changeTicketStatus(openTicket.id, next)}
              />
            </Space>

            <div>
              <SectionLabel>What they sent</SectionLabel>
              <p className="whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 text-[13px] leading-relaxed text-gray-700">
                {openTicket.body}
              </p>
            </div>

            <div>
              <SectionLabel>Thread · newest first</SectionLabel>
              <TicketReplies
                ticketId={openTicket.id}
                clientId={clientId}
                customerName={openTicket.raisedByName}
              />
            </div>

            <div>
              <SectionLabel>Reply</SectionLabel>
              <TicketReplyComposer
                key={openTicket.id}
                customerName={openTicket.raisedByName}
                value={replyText}
                onChange={(text) => {
                  setReplyText(openTicket.id, text)
                  if (replyErrors[openTicket.id]) setReplyErrors((current) => ({ ...current, [openTicket.id]: '' }))
                }}
                error={replyErrors[openTicket.id] ?? ''}
                sentByEmail={replyByEmail[openTicket.id] ?? false}
                onSentByEmailChange={(next) =>
                  setReplyByEmail((current) => ({ ...current, [openTicket.id]: next }))
                }
                sending={replyInFlight[openTicket.id] ?? false}
                onSend={(nextStatus) => {
                  setReplyErrors((current) => ({ ...current, [openTicket.id]: '' }))
                  setReplyInFlight((current) => ({ ...current, [openTicket.id]: true }))
                  sendReply({
                    ticketId: openTicket.id,
                    body: replyText,
                    status: nextStatus,
                    sentByEmail: replyByEmail[openTicket.id] ?? false,
                  })
                }}
              />
            </div>

            <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-[11.5px] leading-relaxed text-gray-500">
              <MessageOutlined className="mr-1.5" />
              The customer answers in Preppy. Their answer lands in this thread, moves the ticket back to
              Reviewing, and emails the support inbox a notice. Replying to a support email reaches only the
              support inbox.
            </p>

            <div>
              <SectionLabel>History</SectionLabel>
              <TicketHistory ticketId={openTicket.id} clientId={clientId} />
            </div>
          </FeatureThemeProvider>
        )}
      </Drawer>
    </FeatureThemeProvider>
  )
}
